<template>
  <div class="p-4">
    <!-- Поиск -->
    <div class="mb-6">
      <div class="relative max-w-md">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
        </div>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Поиск по ID или серийному номеру..."
          class="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200 text-sm"
        />
        <div v-if="searchQuery" class="absolute inset-y-0 right-0 pr-3 flex items-center">
          <button
            @click="clearSearch"
            class="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Состояние загрузки -->
    <div v-if="loading" class="flex justify-center items-center h-40">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
    </div>

    <!-- Таблица -->
    <table v-else class="w-full border-collapse bg-white shadow-md">
      <thead class="bg-gray-100">
        <tr>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">ID</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Продукт</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Серийный номер</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Учёт</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Партия</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Статус</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Кол-во</th>
          <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Создано</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="item in items"
          :key="item.id"
          class="border-b hover:bg-gray-50 cursor-pointer"
          @click="openViewModal(item)"
        >
          <td class="px-4 py-2 text-sm text-gray-700">{{ item.id }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ item.product?.name || '—' }}</td>
          <td class="px-4 py-2 text-sm text-gray-700 font-mono">{{ item.serial_number || '—' }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ item.is_single ? 'Штучный' : 'Количественный' }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ item.batch_manufacture?.number || item.batch_manufacture_id || '—' }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">
            <span :class="statusClass(item.receipt_status)" class="px-2 py-1 rounded-full text-xs font-medium">
              {{ statusLabel(item.receipt_status) }}
            </span>
          </td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ item.count ?? '—' }}</td>
          <td class="px-4 py-2 text-sm text-gray-700">{{ formatDate(item.created_at) }}</td>
        </tr>
      </tbody>
    </table>

    <Pagination
      v-if="pagination.total > 0"
      :current-page="pagination.currentPage"
      :last-page="pagination.lastPage"
      :total="pagination.total"
      :per-page="pagination.perPage"
      @change-page="changePage"
    />

    <!-- Модалка просмотра -->
    <div v-if="viewItem" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" @click.self="viewItem = null">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div class="flex items-center justify-between px-6 py-4 border-b">
          <h2 class="text-lg font-bold">Единица продукции #{{ viewItem.id }}</h2>
          <button @click="viewItem = null" class="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div class="px-6 py-4 space-y-3">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <div class="text-xs text-gray-400">ID</div>
              <div class="font-medium">{{ viewItem.id }}</div>
            </div>
            <div>
              <div class="text-xs text-gray-400">Серийный номер</div>
              <div class="font-mono">{{ viewItem.serial_number || '—' }}</div>
            </div>
            <div>
              <div class="text-xs text-gray-400">Продукт</div>
              <div>{{ viewItem.product?.name || '—' }} <span v-if="viewItem.product?.id" class="text-xs text-gray-400">#{{ viewItem.product.id }}</span></div>
            </div>
            <div>
              <div class="text-xs text-gray-400">Единица измерения</div>
              <div>{{ viewItem.product?.unit?.name || '—' }} <span v-if="viewItem.product?.unit?.id" class="text-xs text-gray-400">#{{ viewItem.product.unit.id }}</span></div>
            </div>
            <div>
              <div class="text-xs text-gray-400">Партия поставщика</div>
              <div>{{ viewItem.batch_manufacture?.number || '—' }} <span v-if="viewItem.batch_manufacture?.id" class="text-xs text-gray-400">#{{ viewItem.batch_manufacture.id }}</span></div>
            </div>
            <div>
              <div class="text-xs text-gray-400">ID партии</div>
              <div>{{ viewItem.batch_manufacture_id || '—' }}</div>
            </div>
            <div>
              <div class="text-xs text-gray-400">Статус</div>
              <div>
                <span :class="statusClass(viewItem.receipt_status)" class="px-2 py-1 rounded-full text-xs font-medium">
                  {{ statusLabel(viewItem.receipt_status) }}
                </span>
              </div>
            </div>
            <div>
              <div class="text-xs text-gray-400">Количество</div>
              <div>{{ viewItem.count ?? '—' }}</div>
            </div>
            <div>
              <div class="text-xs text-gray-400">Учёт</div>
              <div>{{ viewItem.is_single ? 'Штучный' : 'Количественный' }}</div>
            </div>
            <div>
              <div class="text-xs text-gray-400">Производственная задача</div>
              <div>{{ viewItem.production_task_id || '—' }}</div>
            </div>
            <div>
              <div class="text-xs text-gray-400">Приходная накладная</div>
              <div>{{ viewItem.receipt_invoice_product?.receipt_invoice?.id || '—' }}</div>
            </div>
            <div>
              <div class="text-xs text-gray-400">Создано</div>
              <div>{{ formatDate(viewItem.created_at) }}</div>
            </div>
          </div>
        </div>
        <!-- Связанные данные -->
        <div class="px-6 py-4 border-t space-y-5">
          <div>
            <div class="text-sm font-semibold text-gray-700 mb-2">Складские позиции (sp) — все, включая удалённые</div>
            <div v-if="!(viewItem.storehouse_products && viewItem.storehouse_products.length)" class="text-sm text-gray-400">Нет связанных складских позиций</div>
            <table v-else class="w-full text-sm border-collapse">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-3 py-1.5 text-left font-medium text-gray-600">ID</th>
                  <th class="px-3 py-1.5 text-left font-medium text-gray-600">Продукт</th>
                  <th class="px-3 py-1.5 text-left font-medium text-gray-600">Кол-во</th>
                  <th class="px-3 py-1.5 text-left font-medium text-gray-600">Состояние</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="sp in viewItem.storehouse_products" :key="sp.id" class="border-b">
                  <td class="px-3 py-1.5">{{ sp.id }}</td>
                  <td class="px-3 py-1.5">{{ sp.product?.name || '—' }} <span v-if="sp.product?.id" class="text-xs text-gray-400">#{{ sp.product.id }}</span></td>
                  <td class="px-3 py-1.5">{{ sp.count ?? '—' }}</td>
                  <td class="px-3 py-1.5">
                    <span v-if="sp.deleted_at" class="text-red-600 font-medium">Удалена ({{ formatDate(sp.deleted_at) }})</span>
                    <span v-else class="text-green-700">Активна</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <div class="text-sm font-semibold text-gray-700 mb-2">Записи ProductionTaskProduct (через sp) — взятые и нет</div>
            <div v-if="!(viewItem.task_products_via_sp && viewItem.task_products_via_sp.length)" class="text-sm text-gray-400">Нет связанных записей</div>
            <table v-else class="w-full text-sm border-collapse">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-3 py-1.5 text-left font-medium text-gray-600">PTP ID</th>
                  <th class="px-3 py-1.5 text-left font-medium text-gray-600">sp ID</th>
                  <th class="px-3 py-1.5 text-left font-medium text-gray-600">Продукт</th>
                  <th class="px-3 py-1.5 text-left font-medium text-gray-600">Кол-во</th>
                  <th class="px-3 py-1.5 text-left font-medium text-gray-600">Статус</th>
                  <th class="px-3 py-1.5 text-left font-medium text-gray-600">Задача</th>
                  <th class="px-3 py-1.5 text-left font-medium text-gray-600">Заказ</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="ptp in viewItem.task_products_via_sp" :key="ptp.id" class="border-b">
                  <td class="px-3 py-1.5">{{ ptp.id }}</td>
                  <td class="px-3 py-1.5">{{ ptp.storehouse_product_id }}</td>
                  <td class="px-3 py-1.5">{{ ptp.product?.name || '—' }} <span v-if="ptp.product?.id" class="text-xs text-gray-400">#{{ ptp.product.id }}</span></td>
                  <td class="px-3 py-1.5">{{ ptp.count ?? '—' }}</td>
                  <td class="px-3 py-1.5">
                    <span v-if="ptp.is_picked" class="text-green-700 font-medium">Взята</span>
                    <span v-else class="text-gray-500">Запланирована</span>
                  </td>
                  <td class="px-3 py-1.5">
                    <template v-if="ptp.production_task">
                      #{{ ptp.production_task.id }}
                      <span v-if="ptp.production_task.deleted_at" class="text-red-600 font-medium">(удалена)</span>
                    </template>
                    <template v-else>—</template>
                  </td>
                  <td class="px-3 py-1.5">
                    <template v-if="ptp.production_task && ptp.production_task.production_order">
                      #{{ ptp.production_task.production_order.id }}
                      <span v-if="ptp.production_task.production_order.deleted_at" class="text-red-600 font-medium">(удалён)</span>
                    </template>
                    <template v-else>—</template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="px-6 py-4 border-t flex justify-end gap-2">
          <button
            @click="printSticker(viewItem.id)"
            :disabled="printing === viewItem.id"
            class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-gray-400"
          >
            {{ printing === viewItem.id ? 'Печать...' : 'Печать этикетки' }}
          </button>
          <button @click="viewItem = null" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">
            Закрыть
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination.vue';
import { createUrlStateManager } from '@/utils/urlStateManager';

const STATUS_LABELS = {
  visual: 'Визуальный контроль',
  print: 'Печать',
  done: 'Завершён',
  control: 'Контроль',
  defect: 'Брак',
  rework_in_production: 'Доработка',
  smk_approved: 'Прошёл выпуск продукта',
  nc_stopped: 'Остановлено: несоответствие',
};

const STATUS_CLASSES = {
  visual: 'bg-yellow-100 text-yellow-800',
  print: 'bg-blue-100 text-blue-800',
  done: 'bg-green-100 text-green-800',
  control: 'bg-purple-100 text-purple-800',
  defect: 'bg-red-100 text-red-800',
  rework_in_production: 'bg-orange-100 text-orange-800',
  smk_approved: 'bg-green-100 text-green-800',
  nc_stopped: 'bg-red-100 text-red-800',
};

export default {
  components: { Pagination },
  data() {
    return {
      items: [],
      loading: true,
      searchQuery: '',
      printing: null,
      viewItem: null,
      page: 1,
      perPage: 20,
      pagination: {
        currentPage: 1,
        lastPage: 1,
        total: 0,
        perPage: 20,
      },
    };
  },
  created() {
    createUrlStateManager(this, {
      searchQuery: { debounce: 300, urlKey: 'search', default: '' },
      page: { type: 'number', default: 1, urlKey: 'page' },
    }, {
      routeName: 'BatchProducts',
      onUpdate: (key) => {
        if (key === 'searchQuery') {
          this.page = 1;
          this.getItems();
        }
      },
    });

    this.getItems();
  },
  methods: {
    getItems() {
      this.loading = true;
      axios
        .post('/api/batchProduct/getAll', {
          page: this.page,
          per_page: this.perPage,
          search: this.searchQuery.trim() || undefined,
        })
        .then((response) => {
          const d = response.data.data;
          this.items = d.data || [];
          this.pagination = {
            currentPage: d.current_page,
            lastPage: d.last_page,
            total: d.total,
            perPage: d.per_page,
          };
        })
        .catch((error) => {
          console.error(error);
          const msg = error.response?.data?.message || 'Не удалось загрузить данные';
          alert(msg);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    clearSearch() {
      this.searchQuery = '';
    },
    changePage(page) {
      this.page = page;
      this.getItems();
    },
    openViewModal(item) {
      this.viewItem = item;
    },
    async printSticker(batchProductId) {
      this.printing = batchProductId;
      try {
        const response = await axios.post('/api/receiptInvoice/printSticker', {
          batch_product_ids: [batchProductId],
        });
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          alert('Браузер заблокировал всплывающее окно. Разрешите всплывающие окна для этого сайта.');
          return;
        }
        printWindow.document.write(response.data);
        printWindow.document.close();
        printWindow.onload = function () {
          setTimeout(() => {
            printWindow.print();
            printWindow.onafterprint = function () {
              printWindow.close();
            };
          }, 500);
        };
      } catch (error) {
        console.error('Ошибка при печати этикетки:', error);
        const msg = error.response?.data?.message || 'Не удалось распечатать этикетку';
        alert(msg);
      } finally {
        this.printing = null;
      }
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status || '—';
    },
    statusClass(status) {
      return STATUS_CLASSES[status] || 'bg-gray-100 text-gray-800';
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      return d.toLocaleDateString('ru-RU') + ' ' + d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>
